import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { fetchGames, getTeam, getPlayerStats, getTeamStats, fetchSeasons } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart3, Users, Trophy, ArrowLeft, Home } from 'lucide-react';
import { useHighlightTarget, getHighlightTarget } from '../lib/highlightTarget';

export default function StatsPage() {
  const navigate = useNavigate();
  const { currentTeamId } = useGameStore();
  const [team, setTeam] = useState(null);
  const [games, setGames] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [playerStats, setPlayerStats] = useState({});
  const [teamStats, setTeamStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // If we landed here via the AbbreviationKey jump-to-button feature with a
  // stats-col-* target, default the tab to "batting" so the columns render.
  const [activeTab, setActiveTab] = useState(() => {
    const t = getHighlightTarget();
    return t && (t.testId || '').startsWith('stats-col-') ? 'batting' : 'team';
  });

  // AbbreviationKey → stats-column highlighter (AVG, OBP, OPS, SLG, RBI, AB, etc.)
  // Wait until team data has loaded AND we're on the batting tab so the table columns exist.
  useHighlightTarget('/stats', { gameInProgress: !!team && !loading && activeTab === 'batting' });

  useEffect(() => {
    if (!currentTeamId) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentTeamId, selectedSeason]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamData, gamesData, seasonsData] = await Promise.all([
        getTeam(currentTeamId),
        fetchGames(currentTeamId, selectedSeason !== 'all' ? selectedSeason : undefined),
        fetchSeasons(currentTeamId)
      ]);
      
      setTeam(teamData);
      setGames(gamesData);
      setSeasons(seasonsData);
      
      // Get team stats
      const tStats = await getTeamStats(currentTeamId, selectedSeason !== 'all' ? selectedSeason : undefined);
      setTeamStats(tStats);
      
      // Get stats for each player
      const statsPromises = teamData.players?.map(p => 
        getPlayerStats(p.id, selectedSeason !== 'all' ? selectedSeason : undefined)
      ) || [];
      const statsResults = await Promise.all(statsPromises);
      
      const statsMap = {};
      teamData.players?.forEach((p, i) => {
        statsMap[p.id] = statsResults[i];
      });
      setPlayerStats(statsMap);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique seasons from games
  const uniqueSeasons = [...new Set(games.map(g => g.season))].filter(Boolean);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="w-12 h-12 border-4 border-green-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 p-4 md:p-8" data-testid="stats-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex flex-col gap-1">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-stone-500 hover:text-green-900" onClick={() => navigate(-1)} data-testid="back-btn">
                <ArrowLeft size={16} />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-stone-500 hover:text-green-900" onClick={() => navigate('/dashboard')} data-testid="home-btn">
                <Home size={16} />
              </Button>
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-green-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <BarChart3 className="inline mr-2" size={28} />
                STATISTICS
              </h1>
              <p className="text-stone-600">{team?.name}</p>
            </div>
          </div>
          <Select value={selectedSeason} onValueChange={setSelectedSeason}>
            <SelectTrigger className="w-48" data-testid="season-filter">
              <SelectValue placeholder="All Seasons" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Seasons</SelectItem>
              {uniqueSeasons.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="team" data-testid="tab-team">
              <Trophy size={16} className="mr-1" /> Team
            </TabsTrigger>
            <TabsTrigger value="batting" data-testid="tab-batting">
              <Users size={16} className="mr-1" /> Batting
            </TabsTrigger>
          </TabsList>

          {/* Team Stats */}
          <TabsContent value="team">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="border-0 shadow-md">
                <CardContent className="p-4 text-center">
                  <p className="text-4xl font-extrabold text-green-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {teamStats?.wins || 0}
                  </p>
                  <p className="text-sm text-stone-500 uppercase">Wins</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md">
                <CardContent className="p-4 text-center">
                  <p className="text-4xl font-extrabold text-red-500" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {teamStats?.losses || 0}
                  </p>
                  <p className="text-sm text-stone-500 uppercase">Losses</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md">
                <CardContent className="p-4 text-center">
                  <p className="text-4xl font-extrabold text-amber-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {teamStats?.total_runs || 0}
                  </p>
                  <p className="text-sm text-stone-500 uppercase">Runs Scored</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md">
                <CardContent className="p-4 text-center">
                  <p className="text-4xl font-extrabold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {(teamStats?.win_percentage || 0).toFixed(3)}
                  </p>
                  <p className="text-sm text-stone-500 uppercase">Win %</p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Games */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  GAME HISTORY
                </CardTitle>
              </CardHeader>
              <CardContent>
                {games.length === 0 ? (
                  <p className="text-center text-stone-500 py-4">No games recorded</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-stone-200">
                          <th className="text-left py-2 px-3 text-sm font-semibold text-stone-600">Date</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-stone-600">Opponent</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600">Result</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600">Score</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-stone-600">Season</th>
                        </tr>
                      </thead>
                      <tbody>
                        {games.map((game) => {
                          const isWin = game.is_home 
                            ? game.home_score > game.away_score 
                            : game.away_score > game.home_score;
                          return (
                            <tr key={game.id} className="border-b border-stone-100 hover:bg-stone-50">
                              <td className="py-2 px-3">{game.date}</td>
                              <td className="py-2 px-3">
                                {game.is_home ? 'vs' : '@'} {game.opponent_name}
                              </td>
                              <td className="py-2 px-3 text-center">
                                {game.is_complete ? (
                                  <span className={`px-2 py-1 rounded-sm text-xs font-bold ${
                                    isWin ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {isWin ? 'W' : 'L'}
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 rounded-sm text-xs bg-stone-100 text-stone-600">
                                    In Progress
                                  </span>
                                )}
                              </td>
                              <td className="py-2 px-3 text-center font-bold">
                                {game.home_score} - {game.away_score}
                              </td>
                              <td className="py-2 px-3 text-stone-500">{game.season}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Batting Stats */}
          <TabsContent value="batting">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  BATTING STATISTICS
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!team?.players?.length ? (
                  <p className="text-center text-stone-500 py-4">No players on roster</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-stone-200">
                          <th className="text-left py-2 px-3 text-sm font-semibold text-stone-600">#</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-stone-600">Player</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600">G</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600" data-testid="stats-col-AB">AB</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600" data-testid="stats-col-H">H</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600" data-testid="stats-col-2B">2B</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600" data-testid="stats-col-3B">3B</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600" data-testid="stats-col-HR">HR</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600" data-testid="stats-col-RBI">RBI</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600" data-testid="stats-col-BB">BB</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600" data-testid="stats-col-K">K</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600 bg-green-50" data-testid="stats-col-AVG">AVG</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600" data-testid="stats-col-OBP">OBP</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600" data-testid="stats-col-SLG">SLG</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600 bg-amber-50" data-testid="stats-col-OPS">OPS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {team.players.map((player) => {
                          const stats = playerStats[player.id] || {};
                          return (
                            <tr key={player.id} className="border-b border-stone-100 hover:bg-stone-50">
                              <td className="py-2 px-3 font-bold">{player.jersey_number}</td>
                              <td className="py-2 px-3 font-medium">{player.name}</td>
                              <td className="py-2 px-3 text-center">{stats.games_played || 0}</td>
                              <td className="py-2 px-3 text-center">{stats.at_bats || 0}</td>
                              <td className="py-2 px-3 text-center">{stats.hits || 0}</td>
                              <td className="py-2 px-3 text-center">{stats.doubles || 0}</td>
                              <td className="py-2 px-3 text-center">{stats.triples || 0}</td>
                              <td className="py-2 px-3 text-center">{stats.home_runs || 0}</td>
                              <td className="py-2 px-3 text-center">{stats.rbi || 0}</td>
                              <td className="py-2 px-3 text-center">{stats.walks || 0}</td>
                              <td className="py-2 px-3 text-center">{stats.strikeouts || 0}</td>
                              <td className="py-2 px-3 text-center font-bold bg-green-50">
                                {(stats.batting_avg || 0).toFixed(3)}
                              </td>
                              <td className="py-2 px-3 text-center">{(stats.obp || 0).toFixed(3)}</td>
                              <td className="py-2 px-3 text-center">{(stats.slg || 0).toFixed(3)}</td>
                              <td className="py-2 px-3 text-center font-bold bg-amber-50">
                                {(stats.ops || 0).toFixed(3)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
